import PropTypes from 'prop-types';
import { createRef } from 'react';

import { defineMessages } from 'react-intl';

import classNames from 'classnames';

import ImmutablePropTypes from 'react-immutable-proptypes';
import ImmutablePureComponent from 'react-immutable-pure-component';

import { length } from 'stringz';

import { missingAltTextModal, me } from 'mastodon/initial_state';

import AutosuggestInput from 'mastodon/components/autosuggest_input';
import AutosuggestTextarea from 'mastodon/components/autosuggest_textarea';
import { Button } from 'mastodon/components/button';
import { injectIntl } from '@/mastodon/components/intl';
import EmojiPickerDropdown from '../containers/emoji_picker_dropdown_container';
import ItemPickerDropdown from './item_picker_dropdown';
import PollButtonContainer from '../containers/poll_button_container';
import SpoilerButtonContainer from '../containers/spoiler_button_container';
import UploadButtonContainer from '../containers/upload_button_container';
import { countableText } from '../util/counter';

import { CharacterCounter } from './character_counter';
import { EditIndicator } from './edit_indicator';
import { CommandDropdown } from './language_dropdown';
import { DraftsDropdown } from './drafts_dropdown';
import { NavigationBar } from './navigation_bar';
import { PollForm } from "./poll_form";
import { ReplyIndicator } from './reply_indicator';
import { UploadForm } from './upload_form';
import { Warning } from './warning';
import { ComposeQuotedStatus } from './quoted_post';
import { QuotePolicyButton } from './quote_policy_button';
import { VisibilityButton } from './visibility_button';

const allowedAroundShortCode = '><\u0085\u0020\u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u202f\u205f\u3000\u2028\u2029\u0009\u000a\u000b\u000c\u000d';

const messages = defineMessages({
  placeholder: { id: 'compose_form.placeholder', defaultMessage: 'What is on your mind?' },
  spoiler_placeholder: { id: 'compose_form.spoiler_placeholder', defaultMessage: 'Content warning (optional)' },
  publish: { id: 'compose_form.publish', defaultMessage: 'Post' },
  saveChanges: { id: 'compose_form.save_changes', defaultMessage: 'Update' },
  reply: { id: 'compose_form.reply', defaultMessage: 'Reply' },
});

class ComposeForm extends ImmutablePureComponent {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    text: PropTypes.string.isRequired,
    suggestions: ImmutablePropTypes.list,
    spoiler: PropTypes.bool,
    privacy: PropTypes.string,
    spoilerText: PropTypes.string,
    focusDate: PropTypes.instanceOf(Date),
    caretPosition: PropTypes.number,
    preselectDate: PropTypes.instanceOf(Date),
    isSubmitting: PropTypes.bool,
    isChangingUpload: PropTypes.bool,
    isEditing: PropTypes.bool,
    isUploading: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClearSuggestions: PropTypes.func.isRequired,
    onFetchSuggestions: PropTypes.func.isRequired,
    onSuggestionSelected: PropTypes.func.isRequired,
    onChangeSpoilerText: PropTypes.func.isRequired,
    onPaste: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    onPickEmoji: PropTypes.func.isRequired,
    autoFocus: PropTypes.bool,
    withoutNavigation: PropTypes.bool,
    anyMedia: PropTypes.bool,
    missingAltText: PropTypes.bool,
    isInReply: PropTypes.bool,
    singleColumn: PropTypes.bool,
    lang: PropTypes.string,
    maxChars: PropTypes.number,
    redirectOnSuccess: PropTypes.bool,
  };

  state = {
    highlighted: false,
    initialDraft: localStorage.getItem('savedText') || '',
  }; 

  static defaultProps = {
    autoFocus: false,
  };

  constructor(props) {
    super(props);
    this.textareaRef = createRef(null);
  }

  handleChange = (e) => {
    const newText = e.target.value;
    this.props.onChange(newText);
    if (newText.trim() === '') {
      localStorage.removeItem('savedText');
    } else {
      localStorage.setItem('savedText', newText);
    }
  };

  blurOnEscape = (e) => {
    if (['esc', 'escape'].includes(e.key.toLowerCase())) {
      e.target.blur();
    }
  }

  handleKeyDownPost = (e) => {
    if (e.key.toLowerCase() === 'enter' && (e.ctrlKey || e.metaKey)) {
        this.handleSubmit();
        e.preventDefault();
    }
    this.blurOnEscape(e);
  };

  handleKeyDownSpoiler = (e) => {
    if (e.key.toLowerCase() === 'enter') {
      if (e.ctrlKey || e.metaKey) {
        this.handleSubmit();
      } else {
        e.preventDefault();
        this.textareaRef.current?.focus();
      }
    }
    this.blurOnEscape(e);
  };

  getFulltextForCharacterCounting = () => {
    return [this.props.spoiler? this.props.spoilerText: '', countableText(this.props.text)].join('');
  };

  canSubmit = () => {
    const { isSubmitting, isChangingUpload, isUploading, maxChars } = this.props;
    const fulltext = this.getFulltextForCharacterCounting();

    return !(isSubmitting || isUploading || isChangingUpload || length(fulltext) > maxChars);
  };

  handleSubmit = (e) => {
    if (this.props.text !== this.textareaRef.current.value) {
      this.props.onChange(this.textareaRef.current.value);
    }

    if (!this.canSubmit()) {
      return;
    }

    this.props.onSubmit({
      missingAltText: missingAltTextModal && this.props.missingAltText && this.props.privacy !== 'direct',
      quoteToPrivate: this.props.quoteToPrivate,
    });

    localStorage.removeItem('savedText'); 
    this.setState({initialDraft: '' }); 

    if (e) {
      e.preventDefault();
    }
  };

  onSuggestionsClearRequested = () => {
    this.props.onClearSuggestions();
  };

  onSuggestionsFetchRequested = (token) => {
    this.props.onFetchSuggestions(token);
  };

  onSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['text']);
  };

  onSpoilerSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['spoiler_text']);
  };

  handleChangeSpoilerText = (e) => {
    this.props.onChangeSpoilerText(e.target.value);
  };

  handleFocus = () => {
    if (this.composeForm && !this.props.singleColumn) {
      const { left, right } = this.composeForm.getBoundingClientRect();
      if (left < 0 || right > (window.innerWidth || document.documentElement.clientWidth)) {
        this.composeForm.scrollIntoView();
      }
    }
  };

  componentDidMount() {
    this._updateFocusAndSelection({});
    document.addEventListener('click', this.handleGlobalClick);
  }

  componentWillUnmount () {
    if (this.timeout) clearTimeout(this.timeout);
    document.removeEventListener('click', this.handleGlobalClick);
  }

  componentDidUpdate (prevProps) {
    this._updateFocusAndSelection(prevProps);
  }

  _updateFocusAndSelection = (prevProps) => {
    if (this.props.focusDate && this.props.focusDate !== prevProps.focusDate) {
      let selectionEnd, selectionStart;

      if (this.props.preselectDate !== prevProps.preselectDate && this.props.isInReply) {
        selectionEnd   = this.props.text.length;
        selectionStart = this.props.text.search(/\s/) + 1;
      } else if (typeof this.props.caretPosition === 'number') {
        selectionStart = this.props.caretPosition;
        selectionEnd   = this.props.caretPosition;
      } else {
        selectionEnd   = this.props.text.length;
        selectionStart = selectionEnd;
      }

      Promise.resolve().then(() => {
        this.textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
        this.textareaRef.current.focus();
      }).catch(console.error);
    } else if(prevProps.isSubmitting && !this.props.isSubmitting) {
      this.textareaRef.current.focus();
    } else if (this.props.spoiler !== prevProps.spoiler) {
      const mediaJustAdded = this.props.anyMedia && !prevProps.anyMedia;

      if (this.props.spoiler && !mediaJustAdded) {
        this.spoilerText.input.focus();
      } else if (prevProps.spoiler) {
        this.textareaRef.current.focus();
      }
    }
  };

  setSpoilerText = (c) => {
    this.spoilerText = c;
  };

  setRef = c => {
    this.composeForm = c;
  };

  handleEmojiPick = (data) => {
    const { text }     = this.props;
    const position     = this.textareaRef.current.selectionStart;
    const needsSpace   = data.custom && position > 0 && !allowedAroundShortCode.includes(text[position - 1]);

    this.props.onPickEmoji(position, data, needsSpace);
  };

  handleLoadDraft = (text) => {
    this.props.onChange(text);
    setTimeout(() => {
      if (this.textareaRef.current) {
        this.textareaRef.current.focus();
      }
    }, 0);
  };

  handleInsertCommand = (insertText) => {
    const textarea = this.textareaRef.current;
    const position = textarea ? textarea.selectionStart : this.props.text.length;
    const textBefore = this.props.text.slice(0, position);
    const textAfter = this.props.text.slice(position);

    this.props.onChange(textBefore + insertText + textAfter);

    setTimeout(() => {
      if (textarea) {
        let selectionStart = position + insertText.length;
        let selectionEnd = position + insertText.length;

        if (insertText.includes('내용')) {
          selectionStart = position + insertText.indexOf('내용');
          selectionEnd = selectionStart + 2; 
        } 
        else if (insertText.includes('{}')) {
          selectionStart = position + insertText.indexOf('{}') + 1;
          selectionEnd = selectionStart;
        }
        else if (insertText.includes('()')) {
          selectionStart = position + insertText.indexOf('()') + 1;
          selectionEnd = selectionStart;
        }

        textarea.setSelectionRange(selectionStart, selectionEnd);
        textarea.focus();
      }
    }, 0);
  };

  handleGlobalClick = (e) => {
    const isDM = window.location.pathname.startsWith('/direct_message') || window.location.pathname.startsWith('/conversations');
    if (isDM) {
      return;
    }

    const btn = e.target.closest('.복사'); 
    
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      
      const copyDataEl = btn.querySelector('.복사데이터');
      const textToInsert = btn.getAttribute('data-copy') || (copyDataEl ? copyDataEl.textContent : (btn.innerText || btn.textContent));
      
      if (textToInsert) {
        this.handleInsertTextFromButton(textToInsert);
      }
    }
  };

  handleInsertTextFromButton = (insertText) => {
    const textarea = this.textareaRef.current;
    const position = textarea ? textarea.selectionStart : this.props.text.length;
    
    const textBefore = this.props.text.slice(0, position);
    const textAfter = this.props.text.slice(position);

    this.props.onChange(textBefore + insertText + textAfter);

    setTimeout(() => {
      if (textarea) {
        const newPos = position + insertText.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      }
    }, 0);
  };

  render () {
    const { intl, onPaste, onDrop, autoFocus, withoutNavigation, maxChars, isSubmitting } = this.props;
    
    return (
      <>
        <form
          className='compose-form'
          role='region'
          aria-label={intl.formatMessage({
            id: 'tabs_bar.publish',
            defaultMessage: 'New Post'
          })}
          onSubmit={this.handleSubmit}
        >
          <ReplyIndicator />
          {!withoutNavigation && <NavigationBar />}
          <Warning />

          <div className='compose-form__highlightable' ref={this.setRef}>
            <EditIndicator />

            <div className='compose-form__dropdowns'>
              <VisibilityButton disabled={this.props.isEditing} />
              
              <CommandDropdown 
                onSelect={this.handleInsertCommand} 
                text={this.props.text}
                onChange={this.props.onChange}
              />

              <DraftsDropdown 
                currentText={this.props.text} 
                initialDraft={this.state.initialDraft}
                onLoad={this.handleLoadDraft} 
              />
            </div>

            {this.props.spoiler && (
              <div className='spoiler-input'>
                <div className='spoiler-input__border' />

                <AutosuggestInput
                  placeholder={intl.formatMessage(messages.spoiler_placeholder)}
                  value={this.props.spoilerText}
                  disabled={isSubmitting}
                  onChange={this.handleChangeSpoilerText}
                  onKeyDown={this.handleKeyDownSpoiler}
                  ref={this.setSpoilerText}
                  suggestions={this.props.suggestions}
                  onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                  onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                  onSuggestionSelected={this.onSpoilerSuggestionSelected}
                  searchTokens={[':']}
                  id='cw-spoiler-input'
                  className='spoiler-input__input'
                  lang={this.props.lang}
                  spellCheck
                />

                <div className='spoiler-input__border' />
              </div>
            )}

            <AutosuggestTextarea
              ref={this.textareaRef}
              placeholder={intl.formatMessage(messages.placeholder)}
              disabled={isSubmitting}
              value={this.props.text}
              onChange={this.handleChange}
              suggestions={this.props.suggestions}
              onFocus={this.handleFocus}
              onKeyDown={this.handleKeyDownPost}
              onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
              onSuggestionsClearRequested={this.onSuggestionsClearRequested}
              onSuggestionSelected={this.onSuggestionSelected}
              onPaste={onPaste}
              onDrop={onDrop}
              autoFocus={autoFocus}
              lang={this.props.lang}
              size={(this.props.text && this.props.text.size) || 0}
              className='compose-form__input'
            />

            <PollForm />
            <UploadForm />
            <ComposeQuotedStatus />

            <div className='compose-form__footer'>
              <div className='compose-form__actions'>
                <div className='compose-form__buttons'>
                  <UploadButtonContainer />
                  <PollButtonContainer />
                  <SpoilerButtonContainer />
                  <EmojiPickerDropdown onPickEmoji={this.handleEmojiPick} />
                  <ItemPickerDropdown onPickItem={this.handleInsertTextFromButton} />
                  <CharacterCounter max={maxChars} text={this.getFulltextForCharacterCounting()} />
                </div>

                <div className='compose-form__submit'>
                  <Button
                    type='submit'
                    compact
                    disabled={!this.canSubmit()}
                    loading={isSubmitting}
                  >
                    {intl.formatMessage(
                      this.props.isEditing ?
                        messages.saveChanges :
                        (this.props.isInReply ? messages.reply : messages.publish)
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </>
    );
  }
}

export default injectIntl(ComposeForm);